import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import type { ReportFilters } from "@/features/reports/types";
import { useAssetReliabilityReport } from "@/features/assets/reliability";
import { formatDuration } from "@/features/work-orders/format";
import { formatDate } from "@/features/assets/format";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";

export const Route = createFileRoute("/_authenticated/reports/asset-reliability")({
  component: AssetReliabilityReportPage,
});

function AssetReliabilityReportPage() {
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;

  const [filters, setFilters] = useState<ReportFilters>({});
  const [minCount, setMinCount] = useState<number>(0);
  const [currentStatus, setCurrentStatus] = useState<"" | "Down" | "Running">("");

  const branchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const from = filters.dateFrom ? new Date(filters.dateFrom).toISOString() : undefined;
  const to = filters.dateTo ? new Date(filters.dateTo).toISOString() : undefined;

  const { data: rows = [], isLoading } = useAssetReliabilityReport({
    branchId,
    departmentId: filters.departmentId ?? null,
    assetType: filters.assetClass ?? null,
    criticality: filters.criticality ?? null,
    from,
    to,
    minBreakdownCount: minCount,
    currentStatus: currentStatus || null,
  });

  const exportRows = useMemo(
    () => () =>
      rows.map((r) => ({
        Code: r.asset_code,
        Name: r.asset_name,
        Type: r.asset_type ?? "",
        Criticality: r.criticality ?? "",
        Branch: r.branch_name ?? "",
        Department: r.department_name ?? "",
        Location: r.location_name ?? "",
        Breakdowns: r.breakdown_count,
        "Downtime (minutes)": r.total_downtime_minutes,
        "MTTR (minutes)": r.mttr_minutes == null ? "" : Math.round(r.mttr_minutes),
        "Last Breakdown": r.last_breakdown_at ? formatDate(r.last_breakdown_at) : "",
        Status: r.current_status,
        "Active WO": r.active_wo_number ?? "",
      })),
    [rows],
  );

  return (
    <ReportShell
      id="R-53"
      title="Asset Reliability Report"
      description="Breakdown count, downtime and running / down status per asset."
      filters={filters}
      onChange={setFilters}
      enabled={["branchId", "departmentId", "assetClass", "criticality", "dateFrom", "dateTo"]}
      exportRows={exportRows}
      rightActions={
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Min breakdowns</Label>
            <Input
              type="number"
              min={0}
              max={50}
              value={minCount}
              onChange={(e) => setMinCount(Math.max(0, Number(e.target.value) || 0))}
              className="h-9 w-24"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Current status</Label>
            <Select value={currentStatus || "all"} onValueChange={(v) => setCurrentStatus(v === "all" ? "" : (v as "Down" | "Running"))}>
              <SelectTrigger className="h-9 w-32"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="Down">Down</SelectItem>
                <SelectItem value="Running">Running</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      }
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Breakdowns</TableHead>
                  <TableHead className="text-right">Downtime</TableHead>
                  <TableHead className="text-right">MTTR</TableHead>
                  <TableHead>Last Breakdown</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Active WO</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.asset_id}>
                    <TableCell className="font-mono text-xs">
                      <Link to="/assets/$assetId" params={{ assetId: r.asset_id }} className="text-primary hover:underline">
                        {r.asset_code}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">{r.asset_name}</TableCell>
                    <TableCell>{r.asset_type ?? "—"}</TableCell>
                    <TableCell>{r.branch_name ?? "—"}</TableCell>
                    <TableCell>{r.department_name ?? "—"}</TableCell>
                    <TableCell>{r.location_name ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.breakdown_count}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.total_downtime_minutes ? formatDuration(r.total_downtime_minutes) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.mttr_minutes == null ? "—" : formatDuration(Math.round(r.mttr_minutes))}
                    </TableCell>
                    <TableCell className="text-sm">{r.last_breakdown_at ? formatDate(r.last_breakdown_at) : "—"}</TableCell>
                    <TableCell>
                      <Badge variant={r.current_status === "Down" ? "destructive" : "outline"}>{r.current_status}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.active_work_order_id ? (
                        <Link to="/work-orders/$woId" params={{ woId: r.active_work_order_id }} className="text-primary hover:underline">
                          {r.active_wo_number}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} className="py-8 text-center text-sm text-muted-foreground">
                      No assets match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </ReportShell>
  );
}
